import { Box, Flex, Spinner, Text, useDisclosure } from '@chakra-ui/react'
import WhiteBoardCard from './components/WhiteBoardCard'
import { useGetSharedDiagnosticById } from '../../api/api_hooks/shared-diagnostic'
import { useCallback, useEffect, useRef } from 'react'
import { useDiagnosticContext } from '../PatientsBox/PatientProfile'
import Header from './components/Header'
import FinalizeModal from './components/FinalizeModal'
import { DiagnosticImageProcessEnum } from '../../types/diagnostic-image'
import Analyses from '../Analyses'
import { useParams } from 'react-router-dom'
import { useGetSharedDiagnosticImages } from '../../api/api_hooks/shared-diagnostic-images'
import { useTranslation } from 'react-i18next'
import { useTranslatedQueryHook } from '../../utils/useTranslatedQueryHook'

export default function Whiteboard() {
  const { setDiagnosticData } = useDiagnosticContext()
  const {
    data: sharedDiagnosticData,
    isLoading,
    error,
    refetch: refetchSharedDiagnostic,
  } = useTranslatedQueryHook(useGetSharedDiagnosticById())
  const diagnosticId = useParams().diagnosticId
  const { data: diagnosticImages, refetch: refetchDiagnosticImages } =
    useGetSharedDiagnosticImages(diagnosticId!)

  const timeoutId = useRef<NodeJS.Timeout | null>(null)

  const { isOpen, onOpen, onClose } = useDisclosure()

  const latestImages = useRef(diagnosticImages)
  const { t } = useTranslation()

  useEffect(() => {
    latestImages.current = diagnosticImages
  }, [diagnosticImages])

  // Transfer the sharedDiagnosticData to the context to display tags in the sidebar
  useEffect(() => {
    if (sharedDiagnosticData) {
      setDiagnosticData(sharedDiagnosticData)
    }
  }, [sharedDiagnosticData, setDiagnosticData])

  const refetchImagesIfNecessary = useCallback(() => {
    if (timeoutId.current != null) {
      clearTimeout(timeoutId.current)
      timeoutId.current = null
    }

    // Use latestImages.current to use the latest value
    const imagesInProgress = (latestImages.current ?? []).some(
      (image) => image.process === DiagnosticImageProcessEnum.InProgress
    )

    if (!imagesInProgress) {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
        timeoutId.current = null
      }
      return
    }

    const timeoutIdTemp = setTimeout(async () => {
      await refetchDiagnosticImages()
      refetchImagesIfNecessary()
    }, 30000)
    timeoutId.current = timeoutIdTemp
  }, [refetchDiagnosticImages])

  useEffect(() => {
    refetchImagesIfNecessary()
  }, [diagnosticImages, refetchImagesIfNecessary, timeoutId])

  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
        timeoutId.current = null
      }
    }
  }, [])

  if (isLoading) {
    return (
      <Flex justify="center" align="center">
        <Spinner size="xl" />
      </Flex>
    )
  }

  if (error || !sharedDiagnosticData) {
    return (
      <Flex justify="center" align="center">
        <Text color="red.500">{t('shared.errors.errorMsg')}</Text>
      </Flex>
    )
  }

  return (
    <Box
      h={'100%'}
      overflowY={'auto'}
      overflowX={'hidden'}
      p={{
        base: '32px 16px',
        lg: '32px 100px',
        xl: '32px 120px',
      }}
      bg="gray.50"
    >
      <Box
        mb={6}
        p={4}
        bg="orange.50"
        borderWidth="2px"
        borderColor="orange.300"
        borderRadius="xl"
        boxShadow="md"
      >
        <Flex align="center" gap={3}>
          <Text fontSize="2xl">⚠️</Text>
          <Box flex={1}>
            <Text
              fontSize="md"
              fontWeight="600"
              color="orange.800"
              mb={1}
            >
              {t('whiteboard.title')}
            </Text>
            <Text fontSize="sm" color="orange.700" lineHeight="tall">
              {t('whiteboard.disclaimer')}
            </Text>
          </Box>
        </Flex>
      </Box>
      <Header sharedDiagnostic={sharedDiagnosticData} />
      <WhiteBoardCard
        sharedDiagnostic={sharedDiagnosticData}
        onOpen={onOpen}
        diagnosticImages={diagnosticImages ?? []}
        refetchDiagnosticImages={refetchDiagnosticImages}
      />
      <Analyses diagnosticImages={diagnosticImages ?? []} />
      <FinalizeModal
        isOpen={isOpen}
        onClose={onClose}
        refetchSharedDiagnostic={refetchSharedDiagnostic}
      />
    </Box>
  )
}
